/**
 * Created by Abner Sui on 2/16/2020. To view the instance by condition.
 * ------ maintenance history ------
 * Updated by Daniel on 5/27/2021. Add a refresh button.
 * Updated by Daniel on 6/22/2021. Add delete project function.
 * Updated by Daniel on 7/2/2021. Add all filter
 * 10/20/2021 Simon Zhao enabled delta-row-update mode and added refresh method different from the intialization.
 * 2/17/2022 Simon Zhao handle conditional gateway in this component.
 * 8/26/2022 Mitty Zhang mitty added the code for single click to expand row in onRowSelected
 */

import { Component, OnInit, OnDestroy, Input, ChangeDetectionStrategy, HostListener, ElementRef, ChangeDetectorRef } from '@angular/core';
import { catchError, debounceTime, take } from 'rxjs/operators';
import { of, Subject, Subscription } from 'rxjs';
import { GridApi, ColumnApi, GridOptions, ColDef, InitialGroupOrderComparatorParams } from 'ag-grid-community';
import { select, Store } from '@ngrx/store';
import * as _lodash from 'lodash';

import { AppState } from '../../../redux';
import { GetOneProcessDefinition, WorkflowActionTypes } from '../../../tamalelibs/redux/actions/workflow.actions';
import { workflowKanbanFilterSelector } from '../../../tamalelibs/redux/reducers/workflow.reducer';
import { Stage, ProcessInstance, Task, TaskDefinition, ProcessInstanceStatus, TaskType, FieldType, WorkflowKanbanTabType, WorkflowKanbanFilterType, WorkflowKanbanFilterStatus, ExpandGroup, TaskTypes, TaskStatus, ProcessDefinition } from '../../../tamalelibs/models/workflow.model';
import { NotificationStyles } from '../../../widgets/notification/notification.model';
import { SystemUser } from '../../../tamalelibs/models/user.model';
import { BriefConfig } from '../../../widgets/widgets.model';
import { KanbanCardAction } from '../kanban-card/kanban-card.model';
import { ContextMenuConfig, ContextMenuShowAction } from '../../../widgets/tam-context-menu/tam-context-menu.model';
import { SlideSheetActionTypes } from '../../slide-sheet/slide-sheet.model';
import { CustomGroupRowInnerComponentActions } from '../grid-group-row-inner-render/custom-group-row-inner-render.model';
import { FileInfoAggridComponent } from '../file-info-aggrid/file-info-aggrid.component';
import { NumberInfoAggridComponent } from '../number-info-aggrid/numbere-info-aggrid.component';
import { CustomGroupRowInnerRenderer } from '../grid-group-row-inner-render/custom-group-row-inner-renderer.component';
import { SlideSheetService, SlideSheetTriggerSource } from '../../../services/slide-sheet.service';
import { UtilsService } from '../../../tamalelibs/services/utils.service';
import { WorkflowTaskService } from '../../../tamalelibs/services/workflow-task.service';
import { WorkflowService } from '../../../tamalelibs/services/workflow.service';
import { AlertWindowService } from '../../../widgets/alert-window/alert-window.service';
import { lastCustomActionSelector } from '../../../redux/reducers/action-history.reducer';
import { ToastService } from '../../../widgets/toast/toast.service';
import { StoreQuerierService } from '../../../services/store-querier.service';
import { WorkflowKanbanScreenStateService } from '../workflow-kanban-screen-state/workflow-kanban-screen-state.service';
import { ProcessDefinitionViewModel } from '../process-definition-view.model';
import { ArrayHelperService } from '../../../tamalelibs/services/array-helper.service';
import { GridLayoutUtility } from './grid-layout-utilities';
import { ScreenService } from '../../../tamalelibs/services/screen.service';
import { StringLiteralsPipe } from '../../../pipes/translate.pipe';
import { GridUtility } from '../../admin-pages/grid-utility';
import { FileHelperService } from '../../../services/file-helper.service';
import { MAX_PREVIEW_FILE_SIZE } from '../../../tamalelibs/constants/business.constants';
export const DEFAULT_FILTER_STATUS = 2;
const KANBAN_VIEW = { id: 0, text: 'Kanban View', icon: 'kanban' };
const GRID_VIEW = { id: 1, text: 'Grid View', icon: 'grid' };

@Component({
    selector: 'tam-instance-view',
    templateUrl: './instance-view.component.html',
    styleUrls: ['./instance-view.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})

export class InstanceViewComponent implements OnInit, OnDestroy {

    @Input() processDefinition: ProcessDefinitionViewModel;
    @Input() active: boolean;
    /**
     * a flag indicating whether the component has been initialized before
     *
     * in order to avoiding duplicated intialization, set this flag as true once user clicks the corresponding tab.
     *
     * @type {boolean}
     * @memberof InstanceViewComponent
     */
    @Input() isInited: boolean;

    actionContextMenuConfig: ContextMenuConfig;
    allStageList: Array<Stage>;
    allTaskDefinitionList: Array<TaskDefinition>;
    columnDefs: Array<ColDef>;
    contextMenuConfig: ContextMenuConfig;
    currentUser: SystemUser;
    data: Array<ProcessInstance>;
    extraGroupCount: number;
    filterStatus = WorkflowKanbanFilterStatus.ALL;
    firstGroupIndex = 0;
    frameworkComponents: any;
    gridOptions: GridOptions;
    isGroupedByStage: boolean;
    groupCount: number;
    groupedInstanceList: Array<Array<ProcessInstance>>;
    groupRowInnerRenderer;
    groupRowRendererParams;
    instanceCardConfig: BriefConfig = new BriefConfig();
    isLoading = true;
    get isGridView() {
        return this.viewType === 1;
    }
    get isKanbanView() {
        return this.viewType === 0;
    }
    lastGroupIndex: number;
    oneGroupWidth = 332;
    processInstanceList: Array<ProcessInstance>;
    showGroupCount: number;
    sortedGroupList: Array<Stage | TaskDefinition | { id: ProcessInstanceStatus, name: string }>;
    sortedNodeIdArray: Array<string>;
    sortedStageList: Array<Stage>;
    sortedTaskDefinitionList: Array<TaskDefinition>;
    statusContextMenuConfig: ContextMenuConfig;
    viewType = GRID_VIEW.id; // 0 - kanban, 1 - grid

    private _calculatePageSubject$: Subject<any> = new Subject();
    private _columnApi: ColumnApi;
    private _destroySubscriptions: Array<Subscription> = [];
    private _endScrollHandle;
    private _expandGroups: ExpandGroup[] = [];
    private _focusColumn;
    private _gridApi: GridApi;
    private _gridBodyViewport;
    private _groupedInstanceList;
    private _gridLayoutUtility: GridLayoutUtility;
    private _oldPositionForScroll = 0;
    private _rowSelectedIndex = null;
    private _sortedGroupList;

    constructor(
        private _alertWindow: AlertWindowService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _detectChange: ChangeDetectorRef,
        private _element: ElementRef,
        private _fileHelper: FileHelperService,
        private _kanbanStateService: WorkflowKanbanScreenStateService,
        private _storeQuerier: StoreQuerierService,
        private _store: Store<AppState>,
        private _slideSheetService: SlideSheetService,
        private _taskService: WorkflowTaskService,
        private _toast: ToastService,
        private _utils: UtilsService,
        private _workflowService: WorkflowService,
        private _screenService: ScreenService
    ) { }

    ngOnInit() {
        this.currentUser = this._storeQuerier.getCurrentUser();
        this.gridOptions = {
            debug: false,
            suppressPropertyNamesCheck: true,
            immutableColumns: false, // grid 21.1.0 new Properties
            defaultColDef: {
                filter: 'agSetColumnFilter',
                minWidth: 50,
                sortable: true,
                resizable: true,
                cellStyle: (params) => this._getGridCellStyle(params),
                menuTabs: ['filterMenuTab', 'columnsMenuTab', 'generalMenuTab'],
            },
            groupDisplayType: 'groupRows',
            suppressCellFocus: true,
            suppressColumnVirtualisation: true,
            enableBrowserTooltips: true,
            rowGroupPanelShow: 'always',
            rowSelection: 'single',
            sortingOrder: ['asc', 'desc', null],
            maintainColumnOrder: true,
            suppressDragLeaveHidesColumns: true,
            suppressRowGroupHidesColumns: true,
            suppressMakeColumnVisibleAfterUnGroup: true,
            getContextMenuItems: (params) => this._getContextMenuItems(params),
            context: this,
            columnTypes: {
                instance: {}
            },
            initialGroupOrderComparator: (params: InitialGroupOrderComparatorParams) => {
                const a = params.nodeA.key?.toString().toLowerCase() || '';
                const b = params.nodeB.key?.toString().toLowerCase() || '';
                return a < b ? -1 : a > b ? 1 : 0;
            },
        };
        this.frameworkComponents = {
            groupRowInnerRenderer: CustomGroupRowInnerRenderer
        };

        this.groupRowRendererParams = {
            innerRenderer: CustomGroupRowInnerRenderer,
            services: {
                workflowService: this._workflowService
            },
            action$: new Subject()
        };
        const kanbanFilter = this._storeQuerier.queryBySelector(workflowKanbanFilterSelector);
        if (kanbanFilter.has(WorkflowKanbanTabType.PROJECT_BOARD_KANBAN)) {
            const currFilter = kanbanFilter.get(WorkflowKanbanTabType.PROJECT_BOARD_KANBAN);
            this.filterStatus = currFilter[WorkflowKanbanFilterType.SHOW_FILTER_STATUS];
            this.viewType = currFilter[WorkflowKanbanFilterType.VIEW_TYPE] === KANBAN_VIEW.id ? KANBAN_VIEW.id : GRID_VIEW.id;
        }
        this._destroySubscriptions.push(
            this._calculatePageSubject$.pipe(
                debounceTime(500),
            ).subscribe(() => this._calculatePage()),

            this.instanceCardConfig.feedbackSubject$.subscribe(action => this._cardActionHandler(action)),

            this._store.pipe(
                select(lastCustomActionSelector)
            ).subscribe(action => this._actionMessageHandler(action)),
            this._kanbanStateService.refreshSubject$.subscribe(() => {
                this._refresh();
            })
        );
        this._initActionContextMenu();
        this._initContextMenu();
        this._changeFilterCheckedStatus(this.filterStatus);
        this._initData();
        this._setFilterToStore();
        this._detectChange.detectChanges();
    }

    ngOnDestroy() {
        this.getLayoutUtility().destroy();
        this._destroySubscriptions.forEach(item => item.unsubscribe());
    }

    changeStatusFilter(event) {
        this.statusContextMenuConfig.show$.next(new ContextMenuShowAction(event));
        this._closeSideSheet();
        this._detectChange.detectChanges();
    }

    exportToExcel() {
        GridLayoutUtility.exportToXLS(this._gridApi, this._columnApi, this.columnDefs, this._workflowService, this._utils, `${this.processDefinition.definition.name}.xlsx`);
    }

    filterByShowClosed(type: WorkflowKanbanFilterStatus): void {
        // 1. update data source for the kanban view
        this._setFilterSourceByType(type);
        // 2. update the group count for the kanban view.
        this._setPageIndex(type);
        // 3. filter the data source for the grid view
        this._filterData(type);
        // 4. update the selection status for each context menu item.
        this._changeFilterCheckedStatus(type);
        // 5. persist the selected filter in the store.
        this._setFilterToStore();
    }

    /**
     * the function of returning the unique id for each row
     *
     * @param {Task} item
     * @memberof TaskViewComponent
     */
    getRowNodeId = (item: ProcessInstance) => item.id;

    /**
     * gets a grid layout utility instance.
     * @returns the grid layout utility instance.
     */
    getLayoutUtility(): GridLayoutUtility {
        if (!this._gridLayoutUtility) {
            this._gridLayoutUtility = new GridLayoutUtility(this._kanbanStateService.screenId,
                this._store, this._screenService);
        }

        return this._gridLayoutUtility;
    }

    /**
    * If using grouping and groupDisplayType = 'groupRows', then the group row is not focusable. When navigating, the grouping row is skipped. So not use keydown and onCellKeyDown on the grid, use keydown listener
    */
    @HostListener('window:keydown', ['$event'])
    handleKeyDown(event: KeyboardEvent) {
        // grid focus el may be body, cell, row, so if focus el is kendo dialog or input, the keyboard event will return.
        if (document.activeElement.nodeName === 'INPUT' || this._rowSelectedIndex === null) {
            return;
        }
        // 'keyCode' is deprecatedts, so use event.key
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            this._onCellKeyDown(event);
        } else if (event.key === 'Enter') {
            const row = this._gridApi.getDisplayedRowAtIndex(this._rowSelectedIndex);
            if (row && row.group) {
                row.setExpanded(!row.expanded);
            }
        }
    }

    isLayoutSettingBtnVisible() {
        const isPPCUser = this.currentUser && this.currentUser.credential && this.currentUser.credential.publishPubConfiguration;
        const isGridView = this.viewType === GRID_VIEW.id;
        return isPPCUser && isGridView;
    }

    nextPage(event): void {
        if (this.lastGroupIndex < this.groupCount) {
            this.firstGroupIndex++;
            this.lastGroupIndex++;
        }
        this._utils.emptyClick(event);
    }

    lastPage(event): void {
        if (this.firstGroupIndex > 0) {
            this.firstGroupIndex--;
            this.lastGroupIndex--;
        }
        this._utils.emptyClick(event);
    }

    onActionMenu(event): void { }

    onBodyScroll(e) {
        if (e.direction === 'horizontal') {
            clearTimeout(this._endScrollHandle);
            this._endScrollHandle = setTimeout(() => {
                this.groupRowRendererParams.action$.next({
                    type: CustomGroupRowInnerComponentActions.UPDATE_COLUMN_WIDTH_BY_SCROLL,
                    payload: e.left - this._oldPositionForScroll
                });
                this._oldPositionForScroll = e.left;
                this._detectChange.detectChanges();
            }, 250);
        }
    }

    onCellClick(event): void {
        // get the instance from server to get the latest values
        if (event.data) {
            this._workflowService.getProcessInstanceById(event.data.id).pipe(
                catchError(() => {
                    return of(null);
                }),
                take(1)
            ).subscribe(res => {
                const instance = ProcessInstance.parse(res, null, null);
                ProcessInstance.filterOutGateways(instance);
                const mouseEvent = event.event;
                this._focusColumn = event.column;
                if (event.column.userProvidedColDef.type === 'instance') {
                    if (instance && mouseEvent) {
                        this._onSelectInstance(mouseEvent, instance);
                    }
                    return;
                }
                let task: Task;
                const colId: string = event.column.colId;
                const tempIndex: number = colId.indexOf('_');
                if (tempIndex > 0) {
                    const taskDefinitionId: string = colId.substring(0, tempIndex);
                    const fieldDefinitionId: string = colId.substring(tempIndex + 1);
                    const inputTasks: Array<Task> = instance.task.filter(item => item.taskDefinition && item.taskDefinition.type === TaskType.INPUT && item.taskDefinition.id === taskDefinitionId);
                    task = inputTasks.find(item => item.fields.has(fieldDefinitionId) || item.taskDefinition.fields.has(fieldDefinitionId));
                } else {
                    task = instance.task.find(item => item.taskDefinition && item.taskDefinition.id === colId);
                }
                if (instance && mouseEvent && task) {
                    task.processInstance = instance;
                    this._onSelectTask(mouseEvent, task, instance);
                }
            });
        }
    }

    onChangeProcessDefForKanban(): void {
        this.processInstanceList = this.processDefinition.definition.processInstance;
        this.sortedNodeIdArray = this._workflowService.getSortedNodeIdArrayByProcessDefinition(this.processDefinition.definition);
        this.sortedTaskDefinitionList = this._workflowService.getSortedTaskDefinitionListBySortedIds(this.sortedNodeIdArray, this.allTaskDefinitionList);
        const deletedTaskDefs = [];
        // collect removed task definitions as we still need to show those projects stuck at such tasks.
        // those collected task definitions will only be used for determining stages.
        this.processInstanceList.forEach(project => {
            const abandonedTaskDefs = ProcessDefinition.getDifferentTaskDefs(this.processDefinition.definition, project.processDefinition);
            if (abandonedTaskDefs && abandonedTaskDefs.length > 0) {
                deletedTaskDefs.push(...abandonedTaskDefs);
            }
        });
        this.sortedStageList = [];
        this.isGroupedByStage = this._workflowService.getSortedStageList(this.sortedStageList, this.sortedTaskDefinitionList, deletedTaskDefs);
        if (this.isGroupedByStage) { // Group by stage
            this.sortedGroupList = [...this.sortedStageList];
        } else { // Group by task definition
            this.sortedGroupList = [...this.sortedTaskDefinitionList];
        }
        const tempGroupCount = this.sortedGroupList.length;
        this.groupedInstanceList = this._workflowService.getGroupedInstanceList(this.sortedGroupList, this.processInstanceList, this.isGroupedByStage, true);
        this._sortedGroupList = _lodash.cloneDeep(this.sortedGroupList);
        this._groupedInstanceList = _lodash.cloneDeep(this.groupedInstanceList);
        this.extraGroupCount = this.sortedGroupList.length - tempGroupCount;
        this._setFilterSourceByType(this.filterStatus);
        this._setPageIndex(this.filterStatus);
    }

    onChangeProcessDefForGrid(_isInit: boolean = true): void {
        this.sortedNodeIdArray = this._workflowService.getSortedNodeIdArrayByProcessDefinition(this.processDefinition.definition);
        this.processDefinition.definition.taskDefinition = this._workflowService.getSortedTaskDefinitionListBySortedIds(this.sortedNodeIdArray, this.processDefinition.definition.taskDefinition);

        // remove the condition, because the grid need to be refreshed.
        const cellComponent = {};
        cellComponent[FieldType.FILE] = FileInfoAggridComponent;
        cellComponent[FieldType.NUMBER] = NumberInfoAggridComponent;
        // Use an existing value if columnDefs exists, because the column may be changed by user manually
        const columnDefs = this._workflowService.generateInstanceGridColumnDef(this.processDefinition.definition, cellComponent);
        // set the saved column status
        columnDefs.forEach(item => {
            if (item.rowGroup) {
                item.comparator = (valueA, valueB, nodeA, nodeB) => GridUtility.customColumnSortComparator(nodeA, nodeB, item);
            }
            if (!_isInit) {
                item.hide = this.columnDefs?.filter(col => col.colId === item.colId)[0].hide;
            }
        });
        this.getLayoutUtility().getGridLayoutFromStore().subscribe(
            layout => {
                let sortedColumns = [];
                if (layout) {
                    sortedColumns = this.getLayoutUtility().getMergedColDefs(layout, columnDefs);
                }

                this.columnDefs = sortedColumns.length > 0 ? sortedColumns : columnDefs;
            }, err => this.columnDefs = columnDefs
        );

        this.processInstanceList = this.processDefinition.definition.processInstance;
        // update cell data
        this._filterData(this.filterStatus);

        if (this.viewType === GRID_VIEW.id) {
            this.groupCount = this.sortedGroupList.length;
        }

        if (_isInit) {
            setTimeout(() => {
                this._setColumnAutoSizeByWidth();
            }, 0);
        }
    }

    onChangeView(event): void {
        this.actionContextMenuConfig.show$.next(new ContextMenuShowAction(event, ''));
        this._detectChange.detectChanges();
    }

    onColumnMoved(event) {
        if (event.source === 'uiColumnDragged') {
            this._updateGroupRowByColumns(event.column);
        }
    }

    onColumnPinned(event) {
        this._updateGroupRowByColumns(event.column);
    }

    onColumnResized(event) {
        if (event.source === 'uiColumnDragged' && event.finished) {
            this._updateGroupRowByColumns(event.column);
        }
    }

    onColumnRowGroupChanged(event) {
        const columns = event.columns.map((item, i) => ({
            colId: item.colId,
            rowGroupIndex: i
        }));
        this.columnDefs.forEach((column) => {
            const index = columns.findIndex(item => item.colId === column.colId);
            if (index !== -1) {
                column.rowGroupIndex = columns[index].rowGroupIndex;
                column.comparator = (valueA, valueB, nodeA, nodeB) => GridUtility.customColumnSortComparator(nodeA, nodeB, column);
            } else {
                delete column.rowGroupIndex;
            }
        });
        this._gridApi.setColumnDefs(this.columnDefs);
        // set _expandGroups as empty if columns is empty
        if (columns.length === 0) {
            this._expandGroups = [];
        }
        // keep the group row status when column row group changed
        this._setGridRowGroupStatus();
    }

    onColumnVisible(event) {
        this._setColumnVisible(event); // set column visible
        this._updateGroupRowByColumns(event.column);
    }

    onFilterChanged() {
        this.groupRowRendererParams.action$.next({
            type: CustomGroupRowInnerComponentActions.UPDATE_COLUMN_AGGFUNC_VALUE
        });
    }

    onGridReady(params): void {
        this._gridApi = params.api;
        this._columnApi = params.columnApi;
        this._setColumnAutoSizeByWidth();
        this._detectChange.detectChanges();
    }

    onMousedown(event) {
        // grid filter input
        if (event.srcElement && event.srcElement.nodeName === 'INPUT') {
            return;
        }
        // Click grid to open side shite. It will not be opened or closed repeatedly. It will always be open, but only the content will be updated - event.preventDefault()
        // grid key down/up, so not call event.preventDefault()
        // if (event.stopPropagation) {
        //     event.stopPropagation();
        // }
    }

    onRefreshClicked() {
        this._store.dispatch(new GetOneProcessDefinition({ id: this.processDefinition.definition.id }));
    }

    onResize(event): void {
        if (this.viewType === KANBAN_VIEW.id) {
            this._calculatePageSubject$.next();
        } else {
            // Fixed for [TAM-34152]: Column is not auto resized when browser width changed.
            // this._gridApi.sizeColumnsToFit();
        }
    }

    // maintain the value of _expandGroups
    onRowGroupOpened(event) {
        const node: ExpandGroup = this._getParentNode(event.node);
        const index = this._expandGroups.findIndex(item => item.id === node.id);
        if (event.node.expanded) {
            if (index < 0) {
                this._expandGroups.push(node);
            }
        } else {
            if (index >= 0) {
                this._expandGroups.splice(index, 1);
            }
        }
    }

    onRowSelected(row) {
        row.node.expanded = !row.node.expanded;
        this.gridOptions.api.onGroupExpandedOrCollapsed();
        this._rowSelectedIndex = row.rowIndex;
        row.node.setSelected(true);
        this.onRowGroupOpened(row);
    }

    onSelectInstance(event, instance: ProcessInstance): void {
        // get the instance from server to get the latest values
        this._workflowService.getProcessInstanceById(instance.id).pipe(
            catchError(() => {
                return of(null);
            }),
            take(1)
        ).subscribe(res => {
            const _instance = ProcessInstance.parse(res, null, null);
            ProcessInstance.filterOutGateways(_instance);
            this._onSelectInstance(event, _instance);
        });
    }

    onSaveLayoutClicked(event) {
        const utility = this.getLayoutUtility();
        const layoutModel = utility.getGridLayoutFromGrid(this._gridApi, this._columnApi);
        utility.saveGridLayout(layoutModel).subscribe(
            isSucceeded => {
                if (isSucceeded) {
                    this._toast.notify({
                        message: StringLiteralsPipe.translate('workflow.save_layout_settings_successfully'),
                        style: NotificationStyles.Success
                    });
                }
            }
        );
    }

    trackByFn(index, item): string {
        return item.id;
    }

    private _actionMessageHandler(action): void {
        if (action.type === WorkflowActionTypes.PROCESS_INSTANCE_DELETE_SUCCESS) {
            if (action.payload.showNotification) {
                this._toast.notify({
                    message: 'Project ' + action.payload.instance.projectName + ' successfully deleted.',
                    style: NotificationStyles.Success
                });
            }
        } else if (action.type === WorkflowActionTypes.PROCESS_INSTANCE_DELETE_FAILED) {
            if (action.payload.showNotification) {
                this._toast.notify({
                    message: 'Project ' + action.payload.instance.projectName + ' was not found.',
                    style: NotificationStyles.Info
                });
            }
        }
    }

    private _calculatePage(): void {
        const width = window.innerWidth - 64; // 64 is navigation menu width
        let canShowGroupCount = Math.floor(width / this.oneGroupWidth);
        if (canShowGroupCount === 0) {
            canShowGroupCount = 1;
        }
        if (canShowGroupCount >= this.groupCount) {
            // the room is enough to show all groups(stages).
            this.showGroupCount = this.groupCount;
            this.lastGroupIndex = this.showGroupCount + this.firstGroupIndex;
        } else {
            // the room is not enough to show all stages, pagination is needed.
            this.showGroupCount = canShowGroupCount;
            if (!this.lastGroupIndex) {
                // the last index is zero.
                this.lastGroupIndex = this.showGroupCount;
            } else if (this.lastGroupIndex > this.groupCount) {
                // the last index exceeds the group count.
                this.lastGroupIndex = this.groupCount;
                this.firstGroupIndex = this.lastGroupIndex - this.showGroupCount;
            } else if (this.lastGroupIndex - this.firstGroupIndex > this.showGroupCount) {
                // the group range is over-sized.
                this.firstGroupIndex = this.lastGroupIndex - this.showGroupCount;
            }
        }
    }

    private _cardActionHandler(action): void {
        const instance: ProcessInstance = action.payload.instance;
        if (action.type === KanbanCardAction.ON_MEAT_BALL) {
            this.contextMenuConfig.items = [
                { id: '0', text: 'Show Detail', disabled: false },
            ];
            if (this.currentUser.credential.admin) {
                this.contextMenuConfig.items.push({ id: '1', text: 'Delete', disabled: false });
            }
            this._changeDetectorRef.detectChanges();
            this.contextMenuConfig.show$.next(new ContextMenuShowAction(action.payload.event, instance));
            this._utils.emptyClick(action.payload.event);
        }
    }

    private _changeFilterCheckedStatus(type: WorkflowKanbanFilterStatus): void {
        this.statusContextMenuConfig.items.forEach(item => {
            if (item.id === type) {
                item.checked = true;
                this.filterStatus = item.id;
            } else {
                item.checked = false;
            }
        });
    }

    private _closeSideSheet() {
        this._slideSheetService.slideSheetActionSubject$.next({
            type: SlideSheetActionTypes.CLOSE,
            payload: ''
        });
    }

    private _deleteInstance(instance: ProcessInstance): void {
        this._alertWindow.warn('Delete confirmation required',
            ['Are you sure you want to delete this project?'],
            'Delete', 'Cancel').pipe(
                take(1),
            ).subscribe((result: boolean) => {
                if (result) {
                    this._store.dispatch({
                        type: WorkflowActionTypes.PROCESS_INSTANCE_DELETE,
                        payload: {
                            instance: instance,
                            showNotification: true
                        }
                    });
                }
            });
    }

    /**
     * filter the data source for the grid view
     *
     * @private
     * @param {WorkflowKanbanFilterStatus} type the given filter status.
     * @memberof InstanceViewComponent
     */
    private _filterData(type: WorkflowKanbanFilterStatus) {
        switch (type) {
            case WorkflowKanbanFilterStatus.IN_PROCESS: // in progress
                this.data = this.processInstanceList.filter(ins => ins.status !== ProcessInstanceStatus.DONE && ins.status !== ProcessInstanceStatus.REJECTED);
                break;
            case WorkflowKanbanFilterStatus.CLOSED: // close
                this.data = this.processInstanceList.filter(ins => ins.status === ProcessInstanceStatus.DONE || ins.status === ProcessInstanceStatus.REJECTED);
                break;
            case WorkflowKanbanFilterStatus.ALL: // all
                this.data = [...this.processInstanceList];
                break;
            default:
                break;
        }
    }

    private _getGridCellStyle(params) {
        const cellStyleObj = {};
        const leftPinColumns = params.columnApi.getDisplayedLeftColumns();
        if (leftPinColumns.length > 0) {
            if (!params.node.group && params.column.left === 0 && params.node.level !== 0 && params.column.pinned === 'left') {
                const paddingLeft = params.node.level * 30;
                cellStyleObj['padding-left'] = `${paddingLeft}px`;
            }
        } else {
            if (!params.node.group && params.column.left === 0 && params.node.level !== 0) {
                const paddingLeft = params.node.level * 30;
                cellStyleObj['padding-left'] = `${paddingLeft}px`;
            }
        }
        return cellStyleObj;
    }


    private _getContextMenuItems(params) {
        const contextMenuItems = [];
        if (params.api && params.api.clientSideRowModel) {
            if (this.currentUser.credential.admin) {
                contextMenuItems.push({
                    name: StringLiteralsPipe.translate('workflow.delete_project'),
                    action: (function () {
                        const rowData = params.api.clientSideRowModel.rowsToDisplay[params.node.rowIndex].data;
                        params.context._deleteInstance(rowData);
                    })
                });
            }
        }
        if (params.value) {
            if (Array.isArray(params.value)) {
                if (params.value.length > 0 && params.value[0].extension) {
                    if (contextMenuItems.length > 0) {
                        contextMenuItems.push(
                            StringLiteralsPipe.translate('general.separator')
                        );
                    }
                    params.value.forEach(item => {
                        const fileUrl = '/restapi/2.0/workflow/task/' + item.taskId + '/filedata/' + item.id;
                        contextMenuItems.push({
                            name: this._getDisplayFileName(item.fileName),
                            tooltip: item.fileName,
                            subMenu: [
                                {
                                    name: StringLiteralsPipe.translate('general.preview'),
                                    action: () => {
                                        this._fileHelper.previewFile(item.fileId, fileUrl, item.extension.replace('.', ''), item.fileName, false, false, true);
                                    },
                                    disabled: !this._getFilePreviewStatus(item)
                                },
                                {
                                    name: StringLiteralsPipe.translate('general.download'),
                                    action: () => {
                                        this._fileHelper.downloadFile(fileUrl);
                                    }
                                }],
                        });
                    });
                }
            }
        }

        return contextMenuItems;
    }

    // change the filename if it's too long
    private _getDisplayFileName(fileName: string): string {
        let displayFileName = fileName;
        const name = fileName.substring(0, fileName.indexOf('.'));
        const extenion = fileName.substring(fileName.indexOf('.'));
        if (name.length > 60) {
            displayFileName = name.substring(0, 60) + '...' + extenion;
        }
        return displayFileName;
    }

    // whether file can be previewed or not
    private _getFilePreviewStatus(item): boolean {
        let isFilePreview = false;
        if (this._fileHelper.isFilePreviewable(item)) {
            // disable preview if the file size is more than MAX_PREVIEW_FILE_SIZE
            if (item.fileSize <= MAX_PREVIEW_FILE_SIZE) {
                isFilePreview = true;
            }
        }
        return isFilePreview;
    }

    // get parent node by current node
    private _getParentNode(node) {
        const result: ExpandGroup = {
            key: null,
            parent: null,
            id: null
        };
        result.id = this._getParentIds(node);
        result.key = node.key;
        if (node.parent.key) {
            result.parent = this._getParentNode(node.parent);
        }
        return result;
    }

    // get parent ids by the current node
    private _getParentIds(node) {
        const ids = [node.key];
        if (node.parent && node.parent.key) {
            ids.push(this._getParentIds(node.parent));
        }
        return ids.join(',');
    }

    private _initActionContextMenu(): void {
        this.actionContextMenuConfig = new ContextMenuConfig();
        this.actionContextMenuConfig.forceRefresh = true;
        this.actionContextMenuConfig.single = true;
        this.actionContextMenuConfig.showItemIcon = true;
        this.actionContextMenuConfig.stateful = true;
        this.actionContextMenuConfig.items = [
            { id: KANBAN_VIEW.id, text: KANBAN_VIEW.text, icon: KANBAN_VIEW.icon, checked: this.viewType === KANBAN_VIEW.id },
            { id: GRID_VIEW.id, text: GRID_VIEW.text, icon: GRID_VIEW.icon, checked: this.viewType === GRID_VIEW.id },
        ];
        this._destroySubscriptions.push(this.actionContextMenuConfig.events$.subscribe(event => this._onActionMenuSelect(event)));
    }

    private _initContextMenu(): void {
        this.contextMenuConfig = new ContextMenuConfig();
        this.contextMenuConfig.forceRefresh = true;
        this.contextMenuConfig.single = true;
        this.contextMenuConfig.stateful = false;
        this._destroySubscriptions.push(this.contextMenuConfig.events$.subscribe(event => this._onMenuSelect(event)));

        this.statusContextMenuConfig = new ContextMenuConfig();
        this.statusContextMenuConfig.forceRefresh = true;
        this.statusContextMenuConfig.single = true;
        this.statusContextMenuConfig.stateful = true;
        this.statusContextMenuConfig.items = [
            {
                id: 'In Progress',
                text: 'In Progress',
                checked: false,
            },
            {
                id: 'Closed',
                text: 'Closed',
                checked: false,
            },
            {
                id: 'All',
                text: 'All',
                checked: true,
            },
        ];
        this._destroySubscriptions.push(this.statusContextMenuConfig.events$.subscribe(event => this._onMenuSelectFilter(event)));
    }

    private _initData(): void {
        this.allTaskDefinitionList = [];
        this.allStageList = [];
        this._focusColumn = null;
        this.allTaskDefinitionList.push(...this.processDefinition.definition.taskDefinition);
        this.allTaskDefinitionList.forEach(item => {
            if (item.stage && this.allStageList.findIndex(stage => stage.id === item.stage.id) < 0) {
                this.allStageList.push(item.stage);
            }
        });
        this.onChangeProcessDefForKanban();
        this.onChangeProcessDefForGrid();
        this.isLoading = false;
    }

    private _onActionMenuSelect(action): void {
        const type = +action.payload.id;
        switch (type) {
            case 0:
            case 1:
                this.viewType = type;
                this.actionContextMenuConfig.items.forEach((item, index) => item.checked = this.viewType === index);
                this._setFilterToStore();
                this._detectChange.detectChanges();
                break;
            default:
                break;
        }
    }

    private _onCellKeyDown(event) {
        const selectedRow = (rowIndex) => {
            const node = this._gridApi.getDisplayedRowAtIndex(rowIndex);
            if (node.group) {
                this._focusColumn = null;
                this._closeSideSheet();
            }
            this.onRowSelected({ node, event, rowIndex: node.rowIndex, data: node.data });
            this._resetScrollHeight(node);
            let tempColumn = {
                userProvidedColDef: {
                    type: 'instance'
                }
            };
            if (this._focusColumn) {
                tempColumn = this._focusColumn;
            }
            this.onCellClick({ node, event, rowIndex: node.rowIndex, data: node.data, column: tempColumn });
        };
        if (event.key === 'ArrowDown') {
            let nextIndex = this._rowSelectedIndex;
            const totalCount = this._gridApi.getDisplayedRowCount();
            if (++nextIndex < totalCount) {
                selectedRow(nextIndex);
            }
        } else if (event.key === 'ArrowUp') {
            let nextIndex = this._rowSelectedIndex;
            if (--nextIndex > -1) {
                selectedRow(nextIndex);
            }
        }
    }

    private _onMenuSelect(action): void {
        const type = +action.payload.id;
        switch (type) {
            case 0:
                // Show detail
                this.onSelectInstance(action.payload.event, action.payload.data);
                break;
            case 1:
                // Delete
                this._deleteInstance(action.payload.data);
                break;
            default:
                break;
        }
    }

    private _onMenuSelectFilter(action) {
        const type = action.payload.id;
        this.sortedGroupList = _lodash.cloneDeep(this._sortedGroupList);
        this.groupedInstanceList = _lodash.cloneDeep(this._groupedInstanceList);
        this.filterByShowClosed(type);
        this._detectChange.detectChanges();
    }

    private _onSelectInstance(event, instance: ProcessInstance): void {
        if (!instance.task || instance.task.length === 0) {
            throw new Error('invalid project instance found!');
        }

        let firstInprogressTask;
        if (!instance.taskExecuteOrder || instance.taskExecuteOrder.length === 0) {
            // for old versions without this attribute, still take the old logic to lock down the first active task.
            firstInprogressTask = instance.task.find(t => !t.endDate && !!t.taskDefinition);
            if (!firstInprogressTask) {
                ArrayHelperService.sort<Task>(instance.task, 'endDate', false, false);
                firstInprogressTask = instance.task[0];
            }
        } else {
            // for in-progress instances, take the first active task instance.
            const activeTaskStatus = [TaskStatus.CALL_BACK, TaskStatus.IN_PROGRESS, TaskStatus.SEND_BACK, TaskStatus.TODO];
            for (let i = 0; i < instance.taskExecuteOrder.length; i++) {
                const task = instance.taskExecuteOrder[i];
                const activeTask = instance.task.find(t => !!t.taskDefinition && t.taskDefinition.id === task.id && activeTaskStatus.includes(t.status));
                if (activeTask) {
                    firstInprogressTask = activeTask;
                    break;
                }
            }

            if (!firstInprogressTask) {
                // no active task instance found, in other words, only complete or rejected task instances exist.
                const lastTaskId = instance.taskExecuteOrder[instance.taskExecuteOrder.length - 1].id;
                const lastTaskInstance = instance.task.find(t => !!t.taskDefinition && t.taskDefinition.id === lastTaskId);
                if (lastTaskInstance) {
                    firstInprogressTask = lastTaskInstance;
                } else {
                    // the last task in the path indicated by the taskExecuteOrder no found on instance.task, in other words, has not been reached, just return the latest task instance.
                    firstInprogressTask = instance.task[instance.task.length - 1];
                }
            }
        }
        if (firstInprogressTask) {
            firstInprogressTask.processInstance = instance;
        }
        this._onSelectTask(event, firstInprogressTask, instance);
    }

    private _onSelectTask(event, task: Task, instance: ProcessInstance): void {
        this._slideSheetService.showSlideSheetForTask(event, task, instance, this.processDefinition.definition, this._taskService.getTaskEditable(task, this.currentUser), false, SlideSheetTriggerSource.PROJECT_VIEW);
    }

    private _refresh() {
        if (this.processDefinition.definition) {
            this.onChangeProcessDefForKanban();
            this.onChangeProcessDefForGrid(false);

            this._detectChange.detectChanges();
            // reset the expand groups
            if (this._expandGroups) {
                this._setGridRowGroupStatus();
            }
        }
    }

    private _resetScrollHeight(row) {
        const viewportRows = this._gridBodyViewport.clientHeight / row.rowHeight;
        const currentRow = row.rowIndex + 1;
        const scrollTopRows = currentRow + 1 - viewportRows; // currentRow must add a greater number, example 1 to kepp space for scroll for grid
        // need to reset grid body viewport scrollTop, because use groupDisplayType = 'groupRows', the group row is skipped when key down/up. when we no not want skipped the group row, we must re-calcute grid body viewport scrollTop
        this._gridBodyViewport.scrollTop = scrollTopRows * row.rowHeight;
    }

    // set columns auto size by the grid width and total columns width.
    private _setColumnAutoSizeByWidth(): void {
        // get grid view port
        this._gridBodyViewport = this._element.nativeElement.querySelector('.ag-body-viewport');
        if (this._gridBodyViewport) {
            // get the width of the grid
            const gridWidth = this._gridBodyViewport.offsetWidth;
            if (gridWidth > 0) {
                if (this._gridApi && this._columnApi) {
                    this._workflowService.setGridAutoSizeByWidth(gridWidth, this._gridApi, this._columnApi);
                }
            }
        } else {
            // size columns to fit if _gridBodyViewport is null
            if (this._gridApi) {
                this._gridApi.sizeColumnsToFit();
            }
        }
    }

    /**
     * set column visible
     * @param event
     */
    private _setColumnVisible(event) {
        if (this._columnApi) {
            this._columnApi.setColumnVisible(event.column.colId, event.column.visible);
            this.columnDefs.forEach(item => {
                if (item.colId === event.column.colId) {
                    item.hide = !event.column.visible;
                }
            });
        }
    }

    // set grid row group expand status
    private _setGridRowGroupStatus() {
        if (this._expandGroups && this._expandGroups.length > 0) {
            this._gridApi.forEachNode((node) => {
                const nodeKeys = this._getParentIds(node);
                this._expandGroups.forEach(group => {
                    const groupKeys = this._getParentIds(group);
                    // for the case, the first level of grouping may be removed and the second level of grouping should still work.
                    const groupArray = groupKeys.split(',');
                    if (groupKeys === nodeKeys || groupArray.includes(nodeKeys)) {
                        node.setExpanded(true);
                    }
                });
            });
        }
    }

    /**
     * calculate the number of groups which determines whether there's any navigation button for group pagination.
     *
     * @private
     * @param {WorkflowKanbanFilterStatus} type
     * @memberof InstanceViewComponent
     */
    private _setPageIndex(type: WorkflowKanbanFilterStatus): void {
        switch (type) {
            case WorkflowKanbanFilterStatus.IN_PROCESS: // in progress
                this.groupCount = this.sortedGroupList.length - this.extraGroupCount;
                break;
            case WorkflowKanbanFilterStatus.CLOSED: // close
            case WorkflowKanbanFilterStatus.ALL: // all
                this.groupCount = this.sortedGroupList.length;
                break;
            default:
                break;
        }
        this._calculatePage();
    }

    /**
     * update the data source of kanban view per the given filter status.
     *
     * @private
     * @param {WorkflowKanbanFilterStatus} type the given filter status.
     * @memberof InstanceViewComponent
     */
    private _setFilterSourceByType(type: WorkflowKanbanFilterStatus): void {
        switch (type) {
            case WorkflowKanbanFilterStatus.IN_PROCESS:
                break;
            case WorkflowKanbanFilterStatus.CLOSED:
                this.sortedGroupList = this._sortedGroupList.slice(this._sortedGroupList.length - this.extraGroupCount, this._sortedGroupList.length);
                this.groupedInstanceList = this._groupedInstanceList.slice(this._sortedGroupList.length - this.extraGroupCount, this._sortedGroupList.length);
                break;
            case WorkflowKanbanFilterStatus.ALL:
                this.sortedGroupList = this._sortedGroupList;
                this.groupedInstanceList = this._groupedInstanceList;
                break;
            default:
                break;
        }
    }

    /**
     * put the filter status into store
     *
     * @private
     * @memberof InstanceViewComponent
     */
    private _setFilterToStore() {
        this._store.dispatch({
            type: WorkflowActionTypes.SET_WORKFLOW_KANBAN_FILTER,
            payload: {
                key: WorkflowKanbanTabType.PROJECT_BOARD_KANBAN,
                value: {
                    [WorkflowKanbanFilterType.SHOW_FILTER_STATUS]: this.filterStatus,
                    [WorkflowKanbanFilterType.VIEW_TYPE]: this.viewType,
                }
            }
        });
    }

    private _updateGroupRowByColumns(column?) {
        // set column visible, resize column width, pinned column, drag column, redraw rows for indented rows
        if (column && (column.left === 0 || column.oldLeft === 0 || !column.visible)) {
            this._gridApi.redrawRows();
        }
        this.groupRowRendererParams.action$.next({
            type: CustomGroupRowInnerComponentActions.UPDATE_COLUMN_WIDTH_BY_OPERATION
        });
        this._detectChange.detectChanges();
    }
}
